class WagmadePinnedHero extends HTMLElement {
  connectedCallback() {
    this.media = Array.from(this.querySelectorAll('[data-pinned-media]'));
    this.copy = Array.from(this.querySelectorAll('[data-pinned-copy]'));
    this.dots = Array.from(this.querySelectorAll('[data-pinned-dot]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.mediaContainer = this.querySelector('.wagmade-pinned-hero__media');
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    this.sceneCount = Number(this.dataset.sceneCount || this.media.length || 1);
    this.activeIndex = 0;
    this.isPointerDown = false;
    this.pointerStartX = 0;
    this.dragTranslateX = 0;
    this.pendingDragFrame = null;
    this.reduceMotion = this.reduceMotionQuery.matches;
    
    // 使组件可聚焦以支持键盘事件
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    this.bindControls();
    this.bindSwipe();
    this.bindKeyboard();

    this.onMotionPreferenceChange = (event) => {
      this.reduceMotion = event.matches;
    };
    this.reduceMotionQuery.addEventListener('change', this.onMotionPreferenceChange);
    
    // 初始化激活状态
    this.setActive(0);

    // 自动轮播初始化
    this.autoplayEnabled = this.dataset.autoplay !== 'false';
    this.autoplayInterval = Number(this.dataset.autoplayInterval || 6000);
    if (this.autoplayEnabled) {
      this.initAutoplay();
    }
  }

  disconnectedCallback() {
    this.removeTouchListeners();
    this.reduceMotionQuery.removeEventListener('change', this.onMotionPreferenceChange);
    if (this.pendingDragFrame) {
      window.cancelAnimationFrame(this.pendingDragFrame);
    }
    this.stopAutoplay();
  }

  bindControls() {
    // 绑定小圆点点击事件
    this.dots.forEach((dot, idx) => {
      dot.style.cursor = 'pointer';
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goTo(idx);
      });
    });

    // 绑定左右箭头点击事件
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    }
  }

  bindKeyboard() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
  }

  // 指针手势支持 (支持移动端 Swipe 与桌面端鼠标拖拽跟手效果)
  bindSwipe() {
    this.onPointerDown = (e) => {
      if (e.target.closest('button, a, [data-pinned-dot]')) return;
      // 仅限鼠标左键或触摸事件
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.pointerStartX = e.clientX;
      this.isPointerDown = true;
      this.hasMoved = false;
      
      // 锁定指针捕获，防止鼠标滑出容器后事件丢失
      this.setPointerCapture(e.pointerId);
      
      if (this.mediaContainer) {
        // 记录按下时的初始平移百分比比例
        this.startTranslateX = -this.activeIndex * (100 / this.sceneCount);
        // 拖动过程中临时禁用 CSS transition 过渡以实现真正跟手机制
        this.mediaContainer.style.transition = 'none';
      }
      
      if (e.pointerType === 'mouse') {
        e.preventDefault();
      }
    };
    
    this.onPointerMove = (e) => {
      if (!this.isPointerDown) return;
      
      const deltaX = e.clientX - this.pointerStartX;
      if (Math.abs(deltaX) > 5) {
        this.hasMoved = true;
      }
      
      if (this.mediaContainer && this.hasMoved) {
        // 将鼠标移动的物理像素距离转换为滑轨的百分比比例
        const percentOffset = (deltaX / this.offsetWidth) * 100;
        const currentTranslate = this.startTranslateX + (percentOffset / this.sceneCount);
        
        // 增加滑拽超出左/右边界时的 0.3 阻尼效果，防止海报被滑出视野
        let boundTranslate = currentTranslate;
        if (boundTranslate > 0) {
          boundTranslate = boundTranslate * 0.3;
        } else if (boundTranslate < -(this.sceneCount - 1) * (100 / this.sceneCount)) {
          const minTranslate = -(this.sceneCount - 1) * (100 / this.sceneCount);
          const over = boundTranslate - minTranslate;
          boundTranslate = minTranslate + over * 0.3;
        }
        
        this.queueTrackTransform(boundTranslate);
      }
    };
    
    this.onPointerUp = (e) => {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;
      
      try {
        this.releasePointerCapture(e.pointerId);
      } catch (err) {}
      
      if (this.mediaContainer) {
        // 恢复默认的平滑动画过渡
        this.mediaContainer.style.transition = '';
      }

      this.cancelPendingDragFrame();
      
      const deltaX = e.clientX - this.pointerStartX;
      const threshold = 50; // 50 像素触发切换
      
      if (deltaX < -threshold) {
        this.next();
      } else if (deltaX > threshold) {
        this.prev();
      } else {
        // 拖拽距离过小，执行回弹物理效果
        this.restorePosition();
        if (this.autoplayEnabled) {
          this.resetAutoplayTimer();
        }
      }
    };
    
    this.onPointerCancel = (e) => {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;
      try {
        this.releasePointerCapture(e.pointerId);
      } catch (err) {}
      if (this.mediaContainer) {
        this.mediaContainer.style.transition = '';
      }
      this.cancelPendingDragFrame();
      this.restorePosition();
    };

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerCancel);
    
    // 阻止海报内图片的默认拖拽行为，避免产生“图片拖动半透明阴影”
    this.querySelectorAll('img').forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  removeTouchListeners() {
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerCancel);
  }

  restorePosition() {
    if (this.mediaContainer) {
      this.setTrackTransform(-this.activeIndex * (100 / this.sceneCount));
    }
  }

  next() {
    let nextIndex = this.activeIndex + 1;
    if (nextIndex >= this.sceneCount) {
      nextIndex = 0; // 循环
    }
    this.goTo(nextIndex);
  }

  prev() {
    let prevIndex = this.activeIndex - 1;
    if (prevIndex < 0) {
      prevIndex = this.sceneCount - 1; // 循环
    }
    this.goTo(prevIndex);
  }

  goTo(index) {
    if (index === this.activeIndex) return;
    this.setActive(index);
    if (this.autoplayEnabled) {
      this.resetAutoplayTimer();
    }
  }

  initAutoplay() {
    this.autoplayTimer = null;
    this.isAutoplayPaused = false;

    // 悬停暂停
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.resumeAutoplay());
    
    // 聚焦暂停（提高屏幕阅读器可访问性）
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', () => this.resumeAutoplay());

    this.startAutoplay();
  }

  startAutoplay() {
    if (this.reduceMotion) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (!this.isAutoplayPaused && !this.isPointerDown) {
        this.next();
      }
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  pauseAutoplay() {
    this.isAutoplayPaused = true;
  }

  resumeAutoplay() {
    this.isAutoplayPaused = false;
    this.startAutoplay();
  }

  resetAutoplayTimer() {
    this.startAutoplay();
  }

  setActive(index) {
    this.activeIndex = index;
    
    // 更新 media track transform
    if (this.mediaContainer) {
      if (this.reduceMotion) {
        // 关闭滑动动画，通过临时去除 transition 实现即时切换
        const originalTransition = this.mediaContainer.style.transition;
        this.mediaContainer.style.transition = 'none';
        this.setTrackTransform(-index * (100 / this.sceneCount));
        // 强制重绘
        this.mediaContainer.offsetHeight;
        this.mediaContainer.style.transition = originalTransition;
      } else {
        this.setTrackTransform(-index * (100 / this.sceneCount));
      }
    }

    // 更新各元素的激活属性，以匹配 CSS 动画和显隐
    this.media.forEach((item, itemIndex) => {
      item.toggleAttribute('data-active', itemIndex === index);
    });

    this.copy.forEach((item, itemIndex) => {
      item.toggleAttribute('data-active', itemIndex === index);
    });

    this.dots.forEach((item, itemIndex) => {
      item.toggleAttribute('data-active', itemIndex === index);
    });

    // 更新 arrows 的 aria-label
    if (this.prevBtn) {
      this.prevBtn.setAttribute('aria-label', `Go to slide ${((index - 1 + this.sceneCount) % this.sceneCount) + 1}`);
    }
    if (this.nextBtn) {
      this.nextBtn.setAttribute('aria-label', `Go to slide ${((index + 1) % this.sceneCount) + 1}`);
    }
  }

  queueTrackTransform(percent) {
    this.dragTranslateX = percent;
    if (this.pendingDragFrame) return;

    this.pendingDragFrame = window.requestAnimationFrame(() => {
      this.setTrackTransform(this.dragTranslateX);
      this.pendingDragFrame = null;
    });
  }

  setTrackTransform(percent) {
    if (!this.mediaContainer) return;
    this.mediaContainer.style.transform = `translate3d(${percent}%, 0, 0)`;
  }

  cancelPendingDragFrame() {
    if (!this.pendingDragFrame) return;
    window.cancelAnimationFrame(this.pendingDragFrame);
    this.pendingDragFrame = null;
  }
}

if (!customElements.get('wagmade-pinned-hero')) {
  customElements.define('wagmade-pinned-hero', WagmadePinnedHero);
}

// 品类视觉滑动条进度跟踪逻辑
document.addEventListener('DOMContentLoaded', () => {
  initWagmadeCategorySlider();
});

document.addEventListener('shopify:section:load', () => {
  initWagmadeCategorySlider();
});

function initWagmadeCategorySlider() {
  const slider = document.querySelector('.wagmade-category-slider');
  if (!slider || slider.dataset.sliderReady === 'true') return;
  slider.dataset.sliderReady = 'true';
  
  const track = slider.querySelector('[data-category-track]');
  const handle = slider.querySelector('[data-scrollbar-handle]');
  
  if (!track || !handle) return;
  
  function updateScrollbar() {
    const scrollWidth = track.scrollWidth;
    const clientWidth = track.clientWidth;
    const scrollLeft = track.scrollLeft;
    
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) {
      handle.style.width = '100%';
      handle.style.left = '0%';
      return;
    }
    
    const ratio = clientWidth / scrollWidth;
    const percentWidth = ratio * 100;
    
    const scrollPercent = scrollLeft / maxScroll;
    const percentLeft = scrollPercent * (100 - percentWidth);
    
    handle.style.width = `${percentWidth}%`;
    handle.style.left = `${percentLeft}%`;
  }
  
  track.addEventListener('scroll', updateScrollbar, { passive: true });
  window.addEventListener('resize', updateScrollbar);
  
  // 初始化运行
  setTimeout(updateScrollbar, 200); // 延迟一点以确保 layout 计算完成
}
